import {
  Box,
  Grid,
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  Typography,
  TableContainer,
} from "@mui/material";
import { CSVLink } from "react-csv";
import React, { useEffect, useState } from "react";
import { errorMsg } from "../../../utils/toast-msg";
import { FilterList, Search } from "@mui/icons-material";
import ResetIcon from "../../../assets/image/reseticon.svg";
import { CustomInput } from "../../../components/CustomInput";
import PrintIcon from "../../../assets/image/PrintButton.svg";
import { CustomSearchInput } from "./../../../components/CustomSearchInput";
import { getSegmentCountByAirline } from "../../../services/air/air-booking";

const SegmentCountByAirline = () => {
  const [data, setData] = useState([]);
  const [backUpData, setBackUpData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [queryData, setQueryData] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [csvData, setCsvData] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSegmentCountByAirline();

        setData(data?.payload);
        setBackUpData(data?.payload);
        setSearchData(data?.payload);
      } catch (error) {
        if (error?.response?.status === 403) {
          errorMsg("You have no permission to access this page");
        } else {
          console.error("An error occurred while fetching data:", error);
          errorMsg("An unexpected error occurred. Please try again later.");
        }
      }
    })();
  }, []);

  const handleSearchItems = (e) => {
    const searchInput = e.target.value.toLowerCase();
    if (searchInput) {
      const filteredData = data.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchInput)
        )
      );
      setSearchData(filteredData);
    } else {
      setSearchData(data);
    }
  };

  const handleFilterClick = () => {
    setQueryData({});
    setData(backUpData);
    setSearchData(backUpData);
    setShowFilter((prev) => !prev);
  };

  const handleQueryChange = ({ target }) => {
    const newData = { ...queryData };
    newData[target.name] = target.value;
    setQueryData(newData);
  };

  const handleFilterSubmit = async () => {
    const params = {
      fromDate: queryData.fromDate || null,
      toDate: queryData.toDate || null,
    };

    try {
      const { data } = await getSegmentCountByAirline(params);

      setData(data.payload);
      setSearchData(data.payload);
    } catch (error) {
      console.error("Error fetching filtered sgements count:", error);
    }
  };

  const handleFilterReset = () => {
    setQueryData({});
    setData(backUpData);
    setSearchData(backUpData);
  };

  const getAirlineLogoUrl = (airlineCode) => {
    const baseUrl = process.env.REACT_APP_PUBLIC_AIRLINE_LOGO;
    const imgUrl = `${baseUrl}/${airlineCode}.png`;
    return imgUrl;
  };

  return (
    <Box mt={2}>
      <Box
        sx={{
          padding: "16px",
          backgroundColor: "var(--color-bg-white)",
          borderRadius: "12px",
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 },
            borderRadius: "12px",
          }}
          mb={2}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: { xs: 2, sm: "12px" },
              width: "100%",
            }}
          >
            {/* Search Input */}
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", sm: "auto" },
              }}
              className={"placeholderColor"}
            >
              <Search
                style={{
                  position: "absolute",
                  top: "4px",
                  left: "5px",
                  color: "var(--color-draft)",
                  zIndex: 1,
                }}
              />
              <CustomSearchInput
                onChange={handleSearchItems}
                type="text"
                placeholder="Quick Search"
                style={{
                  padding: "0px 0px",
                  boxSizing: "border-box",
                  backgroundColor: "var(--color-bg-hover-sitebar)",
                }}
              />
            </Box>

            {/* Filter Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  paddingRight: "10px",
                  cursor: "pointer",
                }}
              >
                Filter
              </Typography>
              <FilterList
                sx={{
                  color: "var(--color-draft)",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "2px 4px",
                  fontSize: "22px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onClick={handleFilterClick}
              />
            </Box>

            {/* Reset Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                cursor: "pointer",
                width: { xs: "100%", sm: "auto" },
              }}
              onClick={handleFilterReset}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  paddingRight: "10px",
                }}
              >
                Reset
              </Typography>
              <img
                src={ResetIcon}
                alt="Reset"
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "6px 6px",
                  borderRadius: "5px",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              alignItems: "center",
              gap: { xs: 2, sm: "12px" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {/* Print Icon */}
            <CSVLink data={csvData}>
              <img
                src={PrintIcon}
                alt="Print"
                style={{
                  height: "37px",
                  cursor: "pointer",
                }}
              />
            </CSVLink>
          </Box>
        </Box>

        {showFilter ? (
          <Box sx={{ transition: "max-height 0.7s ease" }} mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.5}>
                <CustomInput
                  type="date"
                  name="fromDate"
                  placeholder="Select From Date"
                  onChange={handleQueryChange}
                  value={queryData.fromDate || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.5}>
                <CustomInput
                  type="date"
                  name="toDate"
                  placeholder="Select To Date"
                  onChange={handleQueryChange}
                  value={queryData.toDate || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <button
                  style={{
                    backgroundColor: "var(--color-primary)",
                    color: "var(--color-bg-white)",
                    borderRadius: "5px",
                    padding: "9px 12px",
                    fontFamily: "Outfit",
                    fontSize: "13px",
                    fontWeight: 500,
                    border: "none",
                    cursor: "pointer",
                  }}
                  onClick={handleFilterSubmit}
                >
                  Apply Filter
                </button>
              </Grid>
            </Grid>
          </Box>
        ) : null}

        {/* Table Section */}
        <TableContainer
          sx={{
            backgroundColor: "var(--color-bg-white)",
          }}
        >
          <Table
            sx={{
              minWidth: "900px",
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--color-primary-light)",
                  color: "var(--color-bg-white)",
                  fontWeight: "500",
                  fontSize: "12px",
                }}
              >
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Airline
                </TableCell>
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Total Segment (Expect Non Seg)
                </TableCell>
                <TableCell
                  sx={{
                    color: "var(--color-bg-white)",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "4px",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                  }}
                >
                  Total Segment (Only Non Seg)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(searchData || [])?.map((report) => (
                <React.Fragment key={report.id}>
                  <TableRow
                    sx={{
                      backgroundColor: "var(--color-bg-white)",
                    }}
                  >
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 400,
                        fontSize: "12px",
                        color: "var(--color-bg-hover-siteba)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "4px",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                          lineHeight: "1",
                        }}
                      >
                        <img
                          src={getAirlineLogoUrl(report.airline)}
                          alt={report.airline}
                          style={{
                            width: "25px",
                            height: "25px",
                            objectFit: "contain",
                            verticalAlign: "middle",
                            border: "none",
                          }}
                        />
                      </span>
                      <span
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                          marginLeft: "8px",
                          fontWeight: 500,
                          fontSize: "12px",
                          color: "var(--color-airline-code)",
                          fontFamily: "Outfit",
                          textDecoration: "none",
                        }}
                      >
                        {report.airline}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "4px",
                      }}
                    >
                      {report.totalSegmentExpectNonSeg}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        padding: "4px",
                      }}
                    >
                      {report.totalSegmentOnlyNonSeg}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SegmentCountByAirline;
